/**
 * In-flight request coalescing: identical keys share one Promise until settle.
 * Used by: axios adapter (axiosSetup), dedupedFetch, and any explicit dedupeRequest callers.
 *
 * Intentionally NO short-TTL response cache — avoids stale reads, shared response mutation,
 * and deadlocks when higher-level wrappers also call axios.
 */

const inFlight = new Map();

function stableStringify(value) {
  if (value == null) return "null";
  const t = typeof value;
  if (t !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;
}

/**
 * @param {string} method
 * @param {string} url
 * @param {Record<string, unknown>|null|undefined} params
 * @param {unknown} body
 */
export function buildDedupKey(method, url, params, body) {
  const p = params && typeof params === "object" ? params : {};
  return `${String(method).toUpperCase()}\n${String(url)}\n${stableStringify(p)}\n${stableStringify(body === undefined ? null : body)}`;
}

/**
 * @param {string} key
 * @param {() => Promise<unknown>} executor
 */
export function dedupeRequest(key, executor) {
  const pending = inFlight.get(key);
  if (pending) return pending;

  const promise = (async () => {
    try {
      return await executor();
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
}
